import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createVNode as _createVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "modal__required-text" }
const _hoisted_2 = {
  key: 0,
  class: "modal__selection modal__selection-user-input"
}

import { useUnmanagedBusinessStore } from '@/unmanagedBusinessModule/pages/unmanagedBusinessStore';
import { storeToRefs } from 'pinia';
import { i18n } from '@/i18n';
import TextInput from './TextInput.vue';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { ImportFileType } from '@/coreDomainsModule/entities/unmanagedBusiness';
import SelectInput from '@/corePagesModule/SelectInput/SelectInput.vue';
import SimpleSelectInput from '@/corePagesModule/components/SelectInput.vue';
import { computed, ref, watch } from 'vue';
import { AggregateBusinessRepository } from '@/coreInfrastructureModule/Repositories/AggregateBusinessRepository';
import { useForms } from '@/corePagesModule/composables/useForm';
import { selectOption, userInputOption, inputTypeKeyMap, selectOptionKeyMap, validateRuleKeyMap } from '../constants/formKeyMap';
import { UnmanagedBusinessService } from '../domains/services/UnmanagedBusinessService';
import { FieldItem, ValidateRule } from '@/coreDomainsModule/constant/validateRules';
import { RegisterBusinessRepository } from '@/coreInfrastructureModule/Repositories/RegisterBusinessRepository';
import { GetBusinessNameUseCase } from '@/coreUseCasesModule/getItem/GetBusinessNameUseCase';
import { GetBusinessNumberUseCase } from '@/coreUseCasesModule/getItem/GetBusinessNumberUseCase';
import { GetWarehouseNameUseCase } from '@/coreUseCasesModule/getItem/GetWarehouseNameUseCase';
import { GetWarehouseNumberUseCase } from '@/coreUseCasesModule/getItem/GetWarehouseNumberUseCase';
import { GetSupplierNameUseCase } from '@/coreUseCasesModule/getItem/GetSupplierNameUseCase';
import { GetSupplierNumberUseCase } from '@/coreUseCasesModule/getItem/GetSupplierNumberUseCase';
import { GetCustomerNameUseCase } from '@/coreUseCasesModule/getItem/GetCustomerNameUseCase';
import { GetCustomerNumberUseCase } from '@/coreUseCasesModule/getItem/GetCustomerNumberUseCase';
import { BaseBusinessManageRepository } from '@/coreInfrastructureModule/Repositories/BaseBusinessManageRepository';
import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { IError } from '@/coreInfrastructureModule/interfaces/IError';
import AddOptionSection from '@/corePagesModule/updateBusinessModal/components/AddOptionSection.vue';
import { diContainer } from '@/coreContainer/container';


export default /*@__PURE__*/_defineComponent({
  __name: 'RegisterUnmanagedBusinessModal',
  setup(__props) {

const t = i18n.t;

const store = useUnmanagedBusinessStore();
const { state } = storeToRefs(store);
const { closeModal, openModalByPreviousModal, convertKey, getAddableOptions } = store;

const currentOpenSelectInputName = ref('');

/**
 * APIの構造的に、不必要なリクエストを送らないようにするために行なっている(codeとnameは一対一対応なので片方で埋まっていればもう片方で叩く必要はない)
 * STOPSHIP: SuccessにフックさせているがそもそもAPIの仕様がおかしい気がするのでそこは別途用対応（経緯：https://codeyhq.slack.com/archives/C06T7DCL41L/p1745070581836609?thread_ts=1743985608.078519&cid=C06T7DCL41L）
 */
const firstAutoSetParams = {
  business: false,
  warehouse: false,
  supplier: false,
  customer: false,
};

const aggregateBusinessRepository = new AggregateBusinessRepository();
const registerBusinessRepository = new RegisterBusinessRepository();
const baseBusinessManageRepository = new BaseBusinessManageRepository();

const filedItems = computed((): FieldItem[] => {
  // 選択肢の項目を取得
  const items = selectOption[state.value.selectedFileType].map((name) => {
    return {
      name,
      rules: validateRuleKeyMap[name],
    };
  });
  // 入力項目を取得
  if (state.value.selectedFileType === ImportFileType.Arrival || state.value.selectedFileType === ImportFileType.Inventory) {
    userInputOption.forEach((item) => {
      items.push({
        name: item,
        rules: validateRuleKeyMap[item],
      });
    });
  }
  return items;
});

const { fieldState, allValidate, resetForm } = useForms(filedItems.value);

/**
 * 次へボタンクリック時の処理
 */
const onNext = () => {
  if (allValidate()) {
    filedItems.value.forEach((item) => {
      // リードタイム単位と標準在庫指数単位はconvertUnitToSystemUnitで変換する
      if (item.name === 'leadTimeUnit' || item.name === 'stdInventoryIndicesUnit') {
        state.value.modalInfo.modalState.registerBusiness[item.name] = UnmanagedBusinessService.convertUnitToSystemUnit(
          fieldState[item.name].value.value
        );
        return;
      }
      state.value.modalInfo.modalState.registerBusiness[item.name] = fieldState[item.name].value.value;
    });
    openModalByPreviousModal('registerBusiness', aggregateBusinessRepository, registerBusinessRepository);
  }
};

/**
 * 戻るボタンクリック時の処理
 * モーダルを閉じて、入力項目の値をリセットする
 * また、最初に自動で埋めた項目のフラグをリセットする
 * これにより、次回モーダルを開いた際に再度自動で埋めることができるようにする
 */
const onClose = () => {
  closeModal('registerBusiness');
  resetForm(filedItems.value);
  currentOpenSelectInputName.value = '';
  firstAutoSetParams.business = false;
  firstAutoSetParams.warehouse = false;
  firstAutoSetParams.supplier = false;
  firstAutoSetParams.customer = false;
};

watch(
  () => state.value.modalInfo.isOpen.registerBusiness,
  async (value) => {
    if (value) {
      resetForm(filedItems.value);
      // selectedBusinessの値をfieldStateにセット
      filedItems.value.forEach((item) => {
        if (
          state.value.modalInfo.modalState.registerBusiness[item.name] === '' ||
          state.value.modalInfo.modalState.registerBusiness[item.name] === 0
        )
          return;
        if (item.name === 'leadTimeUnit' || item.name === 'stdInventoryIndicesUnit') {
          fieldState[item.name].setValue(
            UnmanagedBusinessService.convertSystemUnitToDisplayUnit(state.value.modalInfo.modalState.registerBusiness[item.name]),
            false
          );
          return;
        }
        fieldState[item.name].setValue(state.value.modalInfo.modalState.registerBusiness[item.name], false);
      });

      // name優先で自動で埋めていく
      for (const item of filedItems.value) {
        if (item.name === 'businessNumber' && !firstAutoSetParams.business) {
          await setBusinessName();
        } else if (item.name === 'warehouseNumber' && !firstAutoSetParams.warehouse) {
          await setWarehouseName();
        } else if (item.name === 'supplierNumber' && !firstAutoSetParams.supplier) {
          await setSupplierName();
        } else if (item.name === 'customerNumber' && !firstAutoSetParams.customer) {
          await setCustomerName();
        }
      }

      // 次はcodeを自動で埋めていく
      filedItems.value.forEach((item) => {
        if (item.name === 'businessName' && !firstAutoSetParams.business) {
          setBusinessNumber();
        } else if (item.name === 'warehouseName' && !firstAutoSetParams.warehouse) {
          setWarehouseNumber();
        } else if (item.name === 'supplierName' && !firstAutoSetParams.supplier) {
          setSupplierNumber();
        } else if (item.name === 'customerName' && !firstAutoSetParams.customer) {
          setCustomerNumber();
        }
      });
    }
  }
);

const selectionClass = computed(() => {
  return state.value.selectedFileType === ImportFileType.Arrival || state.value.selectedFileType === ImportFileType.Inventory
    ? 'modal__selection--wide'
    : '';
});

/**
 * 入力項目の値をセットする
 * @param key - 入力項目のキー
 * @param value - 入力値
 */
const setValue = (key: string, value: string) => {
  fieldState[key].setValue(value);
  const keyMap = {
    businessNumber: setBusinessName,
    businessName: setBusinessNumber,
    warehouseNumber: setWarehouseName,
    warehouseName: setWarehouseNumber,
    supplierNumber: setSupplierName,
    supplierName: setSupplierNumber,
    customerNumber: setCustomerName,
    customerName: setCustomerNumber,
  };
  if (keyMap[key]) keyMap[key]();
};

/**
 * 汎用的な値設定関数
 * @param useCase - 使用するユースケース
 * @param sourceKey - 取得元のキー
 * @param targetKey - 設定先のキー
 */
const setFieldValue = async (useCase, sourceKey, targetKey) => {
  if (fieldState[sourceKey].value.value === '') return; // 空文字（選択を外す）なら何もしない

  const result: INetworkResult<string | undefined, IError> = await useCase.execute(fieldState[sourceKey].value.value);

  // マッチする値が存在しない場合は何もしない
  if (result.status.type === 'success' && result.status.data !== undefined) {
    fieldState[targetKey].setValue(result.status.data);
    setFirstAutoSetParams(targetKey);
  }
};

/** 商品名を設定する */
const setBusinessName = () => setFieldValue(diContainer.getBusinessNameUseCase, 'businessNumber', 'businessName');
/** 商品番号を設定する */
const setBusinessNumber = () => setFieldValue(diContainer.getBusinessNumberUseCase, 'businessName', 'businessNumber');
/** 置き場名を設定する */
const setWarehouseName = () => setFieldValue(diContainer.getWarehouseNameUseCase, 'warehouseNumber', 'warehouseName');
/** 置き場番号を設定する */
const setWarehouseNumber = () => setFieldValue(diContainer.getWarehouseNumberUseCase, 'warehouseName', 'warehouseNumber');
/** 仕入先名を設定する */
const setSupplierName = () => setFieldValue(diContainer.getSupplierNameUseCase, 'supplierNumber', 'supplierName');
/** 仕入先番号を設定する */
const setSupplierNumber = () => setFieldValue(diContainer.getSupplierNumberUseCase, 'supplierName', 'supplierNumber');
/** 売り上げ先名を設定する */
const setCustomerName = () => setFieldValue(diContainer.getCustomerNameUseCase, 'customerNumber', 'customerName');
/** 売り上げ先番号を設定する */
const setCustomerNumber = () => setFieldValue(diContainer.getCustomerNumberUseCase, 'customerName', 'customerNumber');

/**
 * バリデーションにRequiredがある場合は*を返す
 * @param key　- 入力項目のキー
 */
const getRequiredMark = (key: string | number) => {
  if (validateRuleKeyMap[key].includes(ValidateRule.Required)) {
    return '*';
  }
  return '';
};

/**
 * setter
 * @param targetKey - 自動埋めが成功したキー
 */
const setFirstAutoSetParams = (targetKey: string) => {
  switch (targetKey) {
    case 'businessName':
      firstAutoSetParams.business = true;
      break;
    case 'businessNumber':
      firstAutoSetParams.business = true;
      break;
    case 'warehouseName':
      firstAutoSetParams.warehouse = true;
      break;
    case 'warehouseNumber':
      firstAutoSetParams.warehouse = true;
      break;
    case 'supplierName':
      firstAutoSetParams.supplier = true;
      break;
    case 'supplierNumber':
      firstAutoSetParams.supplier = true;
      break;
    case 'customerName':
      firstAutoSetParams.customer = true;
      break;
    case 'customerNumber':
      firstAutoSetParams.customer = true;
      break;
    default:
      break;
  }
};

/**
 * 新規選択肢追加ボタンを押した際の処理
 * @param type - 選択肢の種類
 * @param name - 名前
 * @param code - コード(Package, Manufacturer, EndUserはなし)
 */
const addNewOption = (type: string, name: string, code?: string) => {
  switch (type) {
    case 'package':
    case 'manufacturer':
    case 'endUser':
      fieldState[type].setValue(name);
      break;
    default:
      if (!code) {
        console.error('エラー:未管理>登録>新規選択肢追加:Codeなし');
        state.value.modalInfo.isOpen.error = true;
        return;
      }
      fieldState[type + 'Name'].setValue(name);
      fieldState[type + 'Number'].setValue(code);
  }
  if (['supplier', 'business', 'warehouse', 'package', 'customer', 'salesDestination'].includes(type)) {
    if (!state.value.modalInfo.newOption.registerMasterInfoTypes.has(type)) {
      state.value.modalInfo.newOption.registerMasterInfoTypes.add(type);
    }
  }

  return;
};

/**
 * 入力欄更新時に、マスタ更新リストからそれを外す関数
 * @param key - 更新した入力欄のキー
 */
const updateRegisterMasterInfoTypesOnChange = (key: string) => {
  const type = key.replace(/(Name|Number)$/, '');
  if (state.value.modalInfo.newOption.registerMasterInfoTypes.has(type)) {
    state.value.modalInfo.newOption.registerMasterInfoTypes.delete(type);
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(InfoModal, {
    "modal-state": _unref(state).modalInfo.isOpen.registerBusiness,
    "back-button-label": _unref(t)('戻る'),
    "next-button-label": _unref(t)('次へ'),
    onOnBack: onClose,
    onOnNext: onNext
  }, {
    title: _withCtx(() => [
      _createTextVNode(_toDisplayString(_unref(t)('必須項目に追記して取引登録')), 1)
    ]),
    description: _withCtx(() => [
      _createTextVNode(_toDisplayString(_unref(t)('既存登録項目に修正し登録してください。選択肢が存在しない場合は新規選択肢を追加して登録してください。')), 1)
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("p", null, _toDisplayString(_unref(t)('*マークは必須')), 1),
        _createElementVNode("div", {
          class: _normalizeClass(['modal__selection', selectionClass.value])
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(state).tableInfo[_unref(state).selectedFileType].selectOptionInfo, (value, key, index) => {
            return (_openBlock(), _createElementBlock(_Fragment, { key: key }, [
              (_unref(inputTypeKeyMap)[key] === 'select')
                ? (_openBlock(), _createBlock(SelectInput, {
                    key: 0,
                    "model-value": _unref(fieldState)[key].value.value,
                    "filter-type": _unref(convertKey)(key.toString()),
                    index: index,
                    label: _unref(t)(`取引登録モーダル選択肢:${key}`) + getRequiredMark(key),
                    placeholder: _unref(t)(`取引登録モーダル選択肢:${key}`),
                    "is-open-prop": currentOpenSelectInputName.value === key,
                    "is-error": _unref(fieldState)[key].isError.value,
                    "error-message": _unref(fieldState)[key].errorMessage.value,
                    "prop-options": _unref(state).tableInfo[_unref(state).selectedFileType].selectOptionInfo[key],
                    "onClick:selectInput": () => (currentOpenSelectInputName.value = key.toString()),
                    "onUpdate:modelValue": 
                (e) => {
                  setValue(key.toString(), e);
                  updateRegisterMasterInfoTypesOnChange(key.toString());
                }
              
                  }, null, 8, ["model-value", "filter-type", "index", "label", "placeholder", "is-open-prop", "is-error", "error-message", "prop-options", "onClick:selectInput", "onUpdate:modelValue"]))
                : (_unref(inputTypeKeyMap)[key] === 'text')
                  ? (_openBlock(), _createBlock(TextInput, {
                      key: 1,
                      "model-value": _unref(fieldState)[key].value.value,
                      label: _unref(t)(`取引登録モーダル選択肢:${key}`) + getRequiredMark(key),
                      placeholder: _unref(t)(`取引登録モーダル選択肢:${key}`),
                      "is-error": _unref(fieldState)[key].isError.value,
                      "error-message": _unref(fieldState)[key].errorMessage.value,
                      "onUpdate:modelValue": (e) => _unref(fieldState)[key].setValue(e)
                    }, null, 8, ["model-value", "label", "placeholder", "is-error", "error-message", "onUpdate:modelValue"]))
                  : _createCommentVNode("", true)
            ], 64))
          }), 128))
        ], 2)
      ]),
      (_unref(state).selectedFileType === _unref(ImportFileType).Arrival || _unref(state).selectedFileType === _unref(ImportFileType).Inventory)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(userInputOption), (value, key) => {
              return (_openBlock(), _createElementBlock(_Fragment, { key: key }, [
                (_unref(inputTypeKeyMap)[value] === 'select')
                  ? (_openBlock(), _createBlock(SimpleSelectInput, {
                      key: 0,
                      "model-value": _unref(fieldState)[value].value.value,
                      index: key + 100,
                      label: _unref(t)(`取引登録モーダル選択肢:${value}`) + getRequiredMark(value),
                      placeholder: _unref(t)(`取引登録モーダル選択肢:${value}`),
                      options: _unref(selectOptionKeyMap)[value]?.map((item) => _unref(t)(item)) ?? [],
                      "is-open-prop": currentOpenSelectInputName.value === value,
                      "is-error": _unref(fieldState)[value].isError.value,
                      "error-message": _unref(fieldState)[value].errorMessage.value,
                      "onClick:selectInput": () => (currentOpenSelectInputName.value = value),
                      "onUpdate:modelValue": (e) => _unref(fieldState)[value].setValue(e)
                    }, null, 8, ["model-value", "index", "label", "placeholder", "options", "is-open-prop", "is-error", "error-message", "onClick:selectInput", "onUpdate:modelValue"]))
                  : (_unref(inputTypeKeyMap)[value] === 'text')
                    ? (_openBlock(), _createBlock(TextInput, {
                        key: 1,
                        "model-value": _unref(fieldState)[value].value.value,
                        label: _unref(t)(`取引登録モーダル選択肢:${value}`) + getRequiredMark(value),
                        placeholder: _unref(t)(`取引登録モーダル選択肢:${value}`),
                        "is-error": _unref(fieldState)[value].isError.value,
                        "error-message": _unref(fieldState)[value].errorMessage.value,
                        "onUpdate:modelValue": (e) => _unref(fieldState)[value].setValue(e)
                      }, null, 8, ["model-value", "label", "placeholder", "is-error", "error-message", "onUpdate:modelValue"]))
                    : _createCommentVNode("", true)
              ], 64))
            }), 128))
          ]))
        : _createCommentVNode("", true),
      _createVNode(AddOptionSection, {
        options: _unref(getAddableOptions)(),
        onSetOption: addNewOption
      }, null, 8, ["options"])
    ], undefined, true),
    _: 1
  }, 8, ["modal-state", "back-button-label", "next-button-label"]))
}
}

})